import { useNavigate, useParams } from "react-router-dom";
import {
  getActorById,
  getWorksByActor,
  getRelatedActors,
  getNewsForActor,
} from "@/utils/catalog";
import FilmCard from "@/components/base/FilmCard";
import ActorMiniCard from "@/pages/detail/components/ActorMiniCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import ShareButton from "@/components/base/ShareButton";
import RelatedNews from "@/pages/detail/components/RelatedNews";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

export default function ActorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const actor = id ? getActorById(id) : undefined;
  const { isActorFavorite, toggleActor } = useFavorites();

  useSeo(
    actor ? `${actor.name}资料 - 葵司` : "页面未找到 | 葵司影视资讯",
    actor
      ? `${actor.name}，${actor.region}${actor.gender}演员，出生于${actor.birth}，代表作品包括${actor.representative}。查看其详细资料、演艺经历与作品列表。`
      : "抱歉，未找到对应的演员资料。",
    actor
      ? `葵司,${actor.name},演员资料,${actor.representative},影视演员`
      : "葵司,演员资料",
  );

  if (!actor) {
    return (
      <div className="max-w-content mx-auto px-4 md:px-6 py-20">
        <EmptyState
          icon="ri-user-line"
          title="未找到该演员资料"
          description="该演员资料可能已更新或链接有误，返回演员列表继续浏览吧。"
          actionLabel="返回演员列表"
          onAction={() => navigate("/category/actor")}
        />
      </div>
    );
  }

  const works = getWorksByActor(actor.id);
  const relatedActors = getRelatedActors(actor, 5);
  const relatedNews = getNewsForActor(actor.id);
  const saved = isActorFavorite(actor.id);

  const infoRows = [
    { label: "性别", value: actor.gender },
    { label: "地区", value: actor.region },
    { label: "出生年月", value: actor.birth },
    { label: "代表作品", value: actor.representative },
  ];

  return (
    <div>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-content mx-auto px-4 md:px-6 py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "演员资料", to: "/category/actor" },
              { label: actor.name },
            ]}
          />
        </div>
      </div>

      <div className="max-w-content mx-auto px-4 md:px-6 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 lg:gap-10">
          <div className="mx-auto w-full max-w-[240px] lg:max-w-none">
            <div className="w-full aspect-square rounded-lg overflow-hidden bg-background-200 border border-background-200">
              <img
                src={actor.avatar}
                alt={`${actor.name}头像`}
                title={`${actor.name} - 葵司演员资料`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-3 h-7 flex items-center rounded-full bg-primary-500 text-background-50 text-xs">
                {actor.region}
              </span>
              <span className="px-3 h-7 flex items-center rounded-full bg-secondary-100 text-secondary-900 text-xs">
                {actor.gender}演员
              </span>
              <span className="px-3 h-7 flex items-center rounded-full bg-accent-100 text-accent-700 text-xs">
                热度 {actor.popularity}
              </span>
            </div>

            <h1 className="mt-4 font-heading font-black text-3xl md:text-4xl text-foreground-950">
              {actor.name}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => toggleActor(actor.id)}
                className={`h-10 px-5 flex items-center gap-2 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  saved
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={saved ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                {saved ? "已收藏" : "收藏演员"}
              </button>
              <ShareButton title={`${actor.name} - 葵司演员资料`} />
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading font-bold text-base text-foreground-950">
                基本资料
              </h2>
              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="shrink-0 text-foreground-500 sm:w-20">
                      {row.label}
                    </dt>
                    <dd className="text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 pt-5 border-t border-background-200">
                <h3 className="font-heading font-bold text-base text-foreground-950">
                  演艺经历
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                  {actor.bio}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="max-w-content mx-auto px-4 md:px-6 pb-10">
        <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-950">
          参与作品
        </h2>
        <p className="mt-2 text-sm text-foreground-500">
          共 {works.length} 部作品，点击卡片查看作品详情。
        </p>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {works.map((f) => (
            <FilmCard key={f.id} film={f} />
          ))}
        </div>
      </section>

      <div className="max-w-content mx-auto px-4 md:px-6 pb-10">
        <RelatedNews
          items={relatedNews}
          title="相关资讯"
          emptyText="暂时没有该演员的相关资讯，敬请期待。"
        />
      </div>

      <section className="max-w-content mx-auto px-4 md:px-6 pb-12">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h2 className="font-heading font-bold text-lg text-foreground-950">
            你可能也喜欢
          </h2>
          <div className="mt-5 flex flex-wrap gap-6">
            {relatedActors.map((a) => (
              <ActorMiniCard key={a.id} actor={a} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}